import { lazy, Suspense } from "react";
import { useSEO } from "@/components/SEOHead";
import {
  MessageSquare, Zap, BarChart3, Shield, Globe, Headphones,
  ArrowRight, CheckCircle2, Send, Phone, Monitor, TrendingUp,
  Search, Share2, Palette, Users, Star, ChevronDown, ChevronUp
} from "lucide-react";
import { useState } from "react";
import { Link } from "wouter";
import {
  ScrollReveal, TiltCard,
  SpotlightSection, MagneticButton, RippleButton
} from "@/components/effects";
import { trackCtaClick } from "@/lib/analytics";
import HomeHeroCarousel from "@/components/HomeHeroCarousel";

// Lazy-loaded components
const HomeStats = lazy(() => import("@/components/HomeStats"));
const HomeWhyChooseUs = lazy(() => import("@/components/HomeWhyChooseUs"));
const HomeTestimonials = lazy(() => import("@/components/HomeTestimonials"));
const HomeBlogPreview = lazy(() => import("@/components/HomeBlogPreview"));
const TrustBadges = lazy(() => import("@/components/TrustBadges"));
const ContactTeamWidget = lazy(() => import("@/components/ContactTeamWidget"));
const AdSlot = lazy(() => import("@/components/AdSlot"));

// Loading fallback
function ComponentSkeleton() {
  return <div className="h-64 bg-gradient-to-b from-gray-100 to-gray-50 animate-pulse rounded-lg" />;
}

// ─── DATA ────────────────────────────────────────────────────────────────────
const messagingServices = [
  { icon: Send, title: "Bulk SMS", desc: "Plan promotional and transactional SMS campaigns with business-focused message controls.", href: "/bulk-sms", color: "from-orange-500 to-red-500" },
  { icon: MessageSquare, title: "WhatsApp Business API", desc: "Explore catalogs, chatbots, templates and rich-media business messaging workflows.", href: "/whatsapp-business-api", color: "from-green-500 to-emerald-500" },
  { icon: Phone, title: "Voice Call & IVR", desc: "Automated voice blasts, toll-free inbound, and multi-level IVR menus that route callers to the right team.", href: "/voice-call", color: "from-blue-500 to-indigo-500" },
  { icon: Zap, title: "OTP & Transactional", desc: "Support account verification and customer-update workflows with purpose-specific messaging.", href: "/bulk-sms", color: "from-purple-500 to-pink-500" },
];

const marketingServices = [
  { icon: Monitor, title: "Google Ads", desc: "Plan and manage paid-search campaigns around defined business goals and transparent reporting.", href: "/google-ads-services", color: "from-yellow-500 to-orange-500" },
  { icon: Search, title: "SEO Services", desc: "Develop a considered SEO plan across technical, on-page and content-focused priorities.", href: "/seo-services", color: "from-teal-500 to-cyan-500" },
  { icon: Share2, title: "Social Media", desc: "Content calendars, paid amplification, and community management across Meta, LinkedIn, and YouTube.", href: "/social-media-marketing", color: "from-pink-500 to-rose-500" },
  { icon: Palette, title: "Website Design", desc: "Fast-loading, mobile-first websites engineered to convert visitors into paying customers.", href: "/website-design", color: "from-violet-500 to-purple-500" },
];

const faqs = [
  { q: "What is bulk SMS and how can my business use it?", a: "Bulk SMS allows you to send messages to thousands of recipients simultaneously. Businesses use it for promotions, OTPs, transactional alerts, appointment reminders, and customer engagement campaigns." },
  { q: "How can I review delivery information after a campaign?", a: "Delivery information is available through the campaign workflow. Your team can use it to review outcomes and plan follow-up activity." },
  { q: "What should I know about DLT registration for bulk SMS?", a: "Commercial SMS workflows in India may require entity, sender and template information through the relevant DLT process. The exact steps depend on the campaign and message type; the team can explain the considerations before you begin." },
];

// ─── FAQ ITEM ────────────────────────────────────────────────────────────────
function FaqItem({ q, a, index }: { q: string; a: string; index: number }) {
  const [open, setOpen] = useState(false);
  return (
    <ScrollReveal delay={index * 80}>
      <div className="border border-white/10 rounded-xl overflow-hidden backdrop-blur-sm bg-white/[0.03] hover:bg-white/[0.06] transition-all duration-300 group">
        <button
          id={`home-faq-toggle-${index}`}
          type="button"
          aria-expanded={open}
          aria-controls={`home-faq-panel-${index}`}
          onClick={() => setOpen(!open)}
          className="w-full flex items-center justify-between p-5 text-left"
        >
          <span className="font-semibold text-white pr-4">{q}</span>
          {open ? <ChevronUp className="w-5 h-5 text-saffron shrink-0" /> : <ChevronDown className="w-5 h-5 text-white/60 group-hover:text-saffron shrink-0 transition-colors" />}
        </button>
        <div
          id={`home-faq-panel-${index}`}
          role="region"
          aria-labelledby={`home-faq-toggle-${index}`}
          className={`overflow-hidden transition-all duration-300 ${open ? "max-h-40 pb-5 px-5" : "max-h-0"}`}
        >
          <p className="text-white/70 text-sm leading-relaxed">{a}</p>
        </div>
      </div>
    </ScrollReveal>
  );
}

// ─── MAIN COMPONENT ──────────────────────────────────────────────────────────
export default function Home() {
  useSEO({
    title: "Bulk SMS Service Provider India | DLT, API & Pricing",
    description: "Compare bulk SMS service options in India for promotional, transactional and OTP messaging, with DLT guidance, API integration and published package pricing.",
    canonical: "https://bharatbulksms.com/",
  });

  return (
    <>
      {/* JSON-LD WebSite Schema for Sitelinks Search Box */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "WebSite",
        "name": "Bharat Bulk SMS",
        "url": "https://bharatbulksms.com",
        "potentialAction": {
          "@type": "SearchAction",
          "target": "https://bharatbulksms.com/search?q={search_term_string}",
          "query-input": "required name=search_term_string"
        }
      }) }} />
      {/* JSON-LD Organization Schema */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Organization",
        "name": "Bharat Bulk SMS",
        "url": "https://bharatbulksms.com",
        "logo": "https://bharatbulksms.com/assets/bharat_bulk_sms_logo_65px_4a34d862_cc287ce8.webp",
        "description": "Bulk SMS, WhatsApp Business API, voice call and digital marketing service options for Indian businesses.",
        "telephone": "+91-7208417941",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "Ostwal Shopping Centre, Opposite Bhayandar East Station, Jesal Park",
          "addressLocality": "Bhayandar East",
          "addressRegion": "Maharashtra",
          "postalCode": "401105",
          "addressCountry": "IN"
        },
        "sameAs": [
          "https://www.facebook.com/bharatbulksms",
          "https://www.instagram.com/bharatbulksms",
          "https://www.linkedin.com/company/bharatbulksms"
        ]
      }) }} />

      {/* ═══════════════════════════════════════════════════════════════════════
          HERO SECTION - Rendered in the initial homepage chunk to avoid an LCP
          request chain and preserve a stable first viewport.
      ═══════════════════════════════════════════════════════════════════════ */}
      <HomeHeroCarousel />

      {/* ═══════════════════════════════════════════════════════════════════════
          STATS BAND - Lazy-loaded with animated counters
      ═══════════════════════════════════════════════════════════════════════ */}
      <Suspense fallback={<ComponentSkeleton />}>
        <HomeStats />
      </Suspense>

      {/* ──────────────────────────────────────────────────────────────────────
          INDUSTRIES - Informational, without unverified client endorsements
      ────────────────────────────────────────────────────────────────────── */}
      <section className="bg-gradient-to-b from-white to-gray-50/50 py-16 lg:py-20">
        <div className="container">
          <ScrollReveal>
            <div className="mx-auto max-w-2xl text-center">
              <span className="font-mono text-xs font-bold uppercase tracking-[0.2em] text-saffron">// Industry use cases</span>
              <h2 className="mt-3 text-3xl font-extrabold text-navy">Communication tools for diverse business needs</h2>
              <p className="mt-4 text-lg leading-relaxed text-gray-600">Explore messaging options for common operational, transactional and engagement use cases across Indian industries.</p>
            </div>
          </ScrollReveal>
          <div className="mx-auto mt-9 flex max-w-4xl flex-wrap justify-center gap-3">
            {["Banking & finance", "Healthcare", "Education", "Retail", "E-commerce", "Logistics", "Real estate", "Travel"].map((industry) => (
              <span key={industry} className="rounded-full border border-saffron/15 bg-white px-4 py-2 text-sm font-medium text-navy shadow-sm">{industry}</span>
            ))}
          </div>
          <div className="mt-8 text-center">
            <Link href="/industries" className="inline-flex items-center font-semibold text-saffron hover:underline">Explore industry solutions <ArrowRight className="ml-1 h-4 w-4" aria-hidden="true" /></Link>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════════
          TRUST BADGES - Certifications & Infrastructure
      ═══════════════════════════════════════════════════════════════════════ */}
      <Suspense fallback={<ComponentSkeleton />}>
        <TrustBadges />
      </Suspense>

      {/* ═══════════════════════════════════════════════════════════════════════
          MESSAGING SERVICES - Glassmorphism cards with tilt & glow
      ═══════════════════════════════════════════════════════════════════════ */}
      <SpotlightSection className="py-20 lg:py-28">
        <div className="container relative z-10">
          <ScrollReveal>
            <div className="grid lg:grid-cols-12 gap-8 items-end mb-14">
              <div className="lg:col-span-7">
                <span className="text-saffron font-bold text-xs uppercase tracking-[0.2em] font-mono">// Messaging Solutions</span>
                <h2 className="text-3xl lg:text-[2.75rem] font-extrabold text-navy mt-3 mb-4 leading-tight">
                  Reach every customer,<br />every time
                </h2>
              </div>
              <div className="lg:col-span-5">
                <p className="text-gray-600 text-lg leading-relaxed">
                  From bulk campaigns to real-time OTPs, our messaging infrastructure handles it all with unmatched reliability.
                </p>
              </div>
            </div>
          </ScrollReveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {messagingServices.map((service, i) => (
              <ScrollReveal key={i} delay={i * 100} direction="up">
                <Link href={service.href}>
                  <TiltCard maxTilt={6}>
                    <div className="h-full p-6 rounded-2xl bg-white border border-gray-100 hover:border-saffron/30 shadow-sm hover:shadow-xl hover:shadow-saffron/10 transition-all duration-500 cursor-pointer group relative overflow-hidden">
                      <div className={`absolute inset-0 bg-gradient-to-br ${service.color} opacity-0 group-hover:opacity-5 transition-opacity duration-500`} />
                      <div className={`absolute top-0 left-0 w-full h-1 bg-gradient-to-r ${service.color} scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left`} />
                      <div className="relative z-10">
                        <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${service.color} flex items-center justify-center mb-5 shadow-lg group-hover:scale-110 group-hover:rotate-3 transition-all duration-300`}>
                          <service.icon className="w-6 h-6 text-white" />
                        </div>
                        <h3 className="font-bold text-lg text-navy mb-2 group-hover:text-saffron transition-colors">{service.title}</h3>
                        <p className="text-sm text-gray-600 leading-relaxed">{service.desc}</p>
                        <div className="mt-4 flex items-center text-saffron text-sm font-semibold opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300">
                          Learn more <ArrowRight className="ml-1 w-3.5 h-3.5" />
                        </div>
                      </div>
                    </div>
                  </TiltCard>
                </Link>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </SpotlightSection>

      {/* ═══════════════════════════════════════════════════════════════════════
          PLATFORM SECTION - Split with parallax image
      ═══════════════════════════════════════════════════════════════════════ */}
      <section className="py-20 lg:py-28 bg-white relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-saffron/30 to-transparent" />
        <div className="container">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <ScrollReveal className="lg:col-span-5" direction="left">
              <TiltCard maxTilt={5}>
                <div className="relative">
                  <div className="absolute -inset-4 bg-gradient-to-br from-saffron/20 via-purple-500/5 to-navy/10 rounded-3xl blur-xl" />
                  <img
                    src="/assets/messaging-platform_81a68975_811d614d.webp"
                    alt="Bharat Bulk SMS messaging platform showing multi-device delivery"
                    className="relative w-full rounded-2xl shadow-2xl border border-gray-100"
                    width={600}
                    height={450}
                    loading="lazy"
                    decoding="async"
                  />
                </div>
              </TiltCard>
            </ScrollReveal>
            <ScrollReveal delay={200} className="lg:col-span-7" direction="right">
              <span className="text-saffron font-bold text-xs uppercase tracking-[0.2em] font-mono">// Why Bharat Bulk SMS</span>
              <h2 className="text-3xl lg:text-[2.75rem] font-extrabold text-navy mt-3 mb-6 leading-tight">
                Built for scale.<br />Designed for India.
              </h2>
              <p className="text-gray-600 text-lg mb-8 leading-relaxed">
                Explore a business messaging platform with campaign controls, delivery information and support options for common communication workflows.
              </p>
              <div className="grid sm:grid-cols-2 gap-3 mb-8">
                {["Campaign planning tools", "Delivery information and reporting", "DLT-related guidance", "Messaging workflow options", "API integration options", "Regional-language message support"].map((item, i) => (
                  <ScrollReveal key={i} delay={i * 60} direction="up">
                    <div className="flex items-start gap-2.5 p-3 rounded-xl bg-gradient-to-r from-cream/80 to-white border border-saffron/10 hover:border-saffron/30 hover:shadow-md transition-all duration-300 group">
                      <CheckCircle2 className="w-4 h-4 text-saffron mt-0.5 shrink-0 group-hover:scale-110 transition-transform" />
                      <span className="text-sm font-medium text-navy">{item}</span>
                    </div>
                  </ScrollReveal>
                ))}
              </div>
              <MagneticButton>
                <Link href="/about" className="inline-flex items-center bg-navy hover:bg-navy/90 text-white font-semibold px-6 py-3 rounded-xl transition-all duration-300 shadow-lg shadow-navy/20 hover:shadow-xl hover:shadow-navy/30 active:scale-[0.97]">
                  Explore Our Infrastructure <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </MagneticButton>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════════
          DIGITAL MARKETING - Animated gradient background
      ═══════════════════════════════════════════════════════════════════════ */}
      <section className="py-20 lg:py-28 relative overflow-hidden bg-gradient-to-br from-[#f8f4ff] via-white to-[#fff8f0]">
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-saffron/30 to-transparent" />
        <div className="absolute top-20 right-20 w-72 h-72 bg-gradient-to-br from-saffron/5 to-purple-500/5 rounded-full blur-3xl animate-pulse" style={{ animationDuration: "5s" }} />
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-gradient-to-tr from-blue-500/5 to-saffron/5 rounded-full blur-3xl animate-pulse" style={{ animationDuration: "7s" }} />
        <div className="container relative z-10">
          <ScrollReveal>
            <div className="max-w-2xl mb-14">
              <span className="text-saffron font-bold text-xs uppercase tracking-[0.2em] font-mono">// Digital Marketing</span>
              <h2 className="text-3xl lg:text-[2.75rem] font-extrabold text-navy mt-3 mb-4 leading-tight">
                Grow your business<br />beyond messaging
              </h2>
              <p className="text-gray-600 text-lg">
                Full-stack digital marketing services to drive leads, boost visibility, and accelerate revenue growth.
              </p>
            </div>
          </ScrollReveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {marketingServices.map((service, i) => (
              <ScrollReveal key={i} delay={i * 100} direction="up">
                <Link href={service.href}>
                  <TiltCard maxTilt={6}>
                    <div className="h-full p-6 rounded-2xl bg-white/80 backdrop-blur-sm border border-gray-100 hover:border-saffron/30 shadow-sm hover:shadow-xl hover:shadow-purple-500/10 transition-all duration-500 cursor-pointer group relative overflow-hidden">
                      <div className={`absolute inset-0 bg-gradient-to-br ${service.color} opacity-0 group-hover:opacity-5 transition-opacity duration-500`} />
                      <div className={`absolute top-0 left-0 w-full h-1 bg-gradient-to-r ${service.color} scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left`} />
                      <div className="relative z-10">
                        <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${service.color} flex items-center justify-center mb-5 shadow-lg group-hover:scale-110 group-hover:-rotate-3 transition-all duration-300`}>
                          <service.icon className="w-6 h-6 text-white" />
                        </div>
                        <h3 className="font-bold text-lg text-navy mb-2 group-hover:text-saffron transition-colors">{service.title}</h3>
                        <p className="text-sm text-gray-600 leading-relaxed">{service.desc}</p>
                        <div className="mt-4 flex items-center text-saffron text-sm font-semibold opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300">
                          Learn more <ArrowRight className="ml-1 w-3.5 h-3.5" />
                        </div>
                      </div>
                    </div>
                  </TiltCard>
                </Link>
              </ScrollReveal>
            ))}
          </div>
          <ScrollReveal delay={400}>
            <div className="mt-10">
              <MagneticButton>
                <Link href="/services" className="inline-flex items-center border-2 border-navy text-navy hover:bg-navy hover:text-white font-semibold px-6 py-3 rounded-xl transition-all duration-300">
                  Explore All 24 Services <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </MagneticButton>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ──────────────────────────────────────────────────────────────────────
          CAMPAIGN PLANNING - Original educational guidance
      ────────────────────────────────────────────────────────────────────── */}
      <section className="bg-[#f8f4ff] py-16 lg:py-20">
        <div className="container">
          <ScrollReveal>
            <div className="mx-auto max-w-2xl text-center">
              <span className="font-mono text-xs font-bold uppercase tracking-[0.2em] text-saffron">// Campaign planning</span>
              <h2 className="mt-3 text-3xl font-extrabold text-navy">Start with a message your audience expects</h2>
              <p className="mt-4 text-lg leading-relaxed text-gray-600">Choose the right channel, use an approved message template where required, include a clear purpose and review the delivery information after sending.</p>
            </div>
          </ScrollReveal>
          <div className="mx-auto mt-10 grid max-w-5xl gap-5 md:grid-cols-3">
            {[
              ["1", "Define the purpose", "Decide whether the message is promotional, transactional, OTP-related or customer support."],
              ["2", "Prepare responsibly", "Use accurate recipient data, consent-aware practices and the sender or template steps relevant to the channel."],
              ["3", "Review and improve", "Check delivery information, handle customer responses appropriately and refine your next campaign."],
            ].map(([number, title, description]) => (
              <div key={number} className="rounded-2xl border border-saffron/10 bg-white p-6 shadow-sm">
                <span className="font-mono text-sm font-bold text-saffron">{number}</span>
                <h3 className="mt-3 text-lg font-bold text-navy">{title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-gray-600">{description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════════
          WHY CHOOSE US - Lazy-loaded Glassmorphism grid with spotlight
      ═══════════════════════════════════════════════════════════════════════ */}
      <Suspense fallback={<ComponentSkeleton />}>
        <HomeWhyChooseUs />
      </Suspense>

      {/* ═══════════════════════════════════════════════════════════════════════
          CONTACT FORM - Split layout with glassmorphism
      ═══════════════════════════════════════════════════════════════════════ */}
      <section className="py-20 lg:py-28 bg-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `radial-gradient(circle, #0a0e1a 1px, transparent 1px)`,
          backgroundSize: '24px 24px'
        }} />
        <div className="absolute top-[-5%] right-[-5%] w-[300px] h-[300px] rounded-full bg-gradient-to-br from-saffron/5 to-transparent blur-[60px]" />
        <div className="absolute bottom-[-5%] left-[-5%] w-[250px] h-[250px] rounded-full bg-gradient-to-tr from-blue-500/5 to-transparent blur-[60px]" />
        <div className="absolute top-12 left-[8%] w-14 h-14 border border-navy/[0.04] rotate-45 hidden lg:block" />
        <div className="absolute bottom-16 right-[6%] w-8 h-8 border border-saffron/[0.08] rotate-12 hidden lg:block" />
        <div className="absolute top-[20%] right-[3%] w-2 h-2 bg-saffron/20 rounded-full hidden lg:block" />
        <div className="absolute bottom-[30%] left-[2%] w-1.5 h-1.5 bg-navy/20 rounded-full hidden lg:block" />
        <div className="container relative z-10">
          <div className="grid lg:grid-cols-2 gap-16">
            <ScrollReveal direction="left">
              <span className="text-saffron font-bold text-xs uppercase tracking-[0.2em] font-mono">// Get in Touch</span>
              <h2 className="text-3xl font-extrabold text-navy mt-3 mb-4">Start your messaging journey</h2>
              <p className="text-gray-600 mb-8">Share your requirements and our team will respond during business hours.</p>
              <form className="space-y-5" onSubmit={(e) => { e.preventDefault(); window.open('https://wa.me/917208417941?text=Hi, I want to know about Bharat Bulk SMS services', '_blank', 'noopener,noreferrer'); }}>
                <div className="grid sm:grid-cols-2 gap-4">
                  <input type="text" placeholder="Full Name *" required className="w-full px-4 py-3.5 rounded-xl border border-gray-200 focus:border-saffron focus:ring-2 focus:ring-saffron/20 outline-none transition-all text-sm bg-gray-50/50 hover:bg-white" aria-label="Full Name" />
                  <input type="tel" placeholder="Phone Number *" required className="w-full px-4 py-3.5 rounded-xl border border-gray-200 focus:border-saffron focus:ring-2 focus:ring-saffron/20 outline-none transition-all text-sm bg-gray-50/50 hover:bg-white" aria-label="Phone Number" />
                </div>
                <input type="email" placeholder="Business Email *" required className="w-full px-4 py-3.5 rounded-xl border border-gray-200 focus:border-saffron focus:ring-2 focus:ring-saffron/20 outline-none transition-all text-sm bg-gray-50/50 hover:bg-white" aria-label="Business Email" />
                <select className="w-full px-4 py-3.5 rounded-xl border border-gray-200 focus:border-saffron focus:ring-2 focus:ring-saffron/20 outline-none transition-all text-sm bg-gray-50/50 hover:bg-white text-gray-600" aria-label="Service Interest">
                  <option value="bulk-sms">Bulk SMS</option>
                  <option value="whatsapp">WhatsApp Business API</option>
                  <option value="voice">Voice Call</option>
                  <option value="google-ads">Google Ads</option>
                  <option value="seo">SEO Services</option>
                  <option value="social-media">Social Media Marketing</option>
                  <option value="other">Other</option>
                </select>
                <textarea placeholder="Tell us about your requirements..." rows={4} className="w-full px-4 py-3.5 rounded-xl border border-gray-200 focus:border-saffron focus:ring-2 focus:ring-saffron/20 outline-none transition-all text-sm resize-none bg-gray-50/50 hover:bg-white" aria-label="Message"></textarea>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" required className="mt-1 w-4 h-4 text-saffron border-gray-300 rounded focus:ring-saffron" />
                  <span className="text-xs text-gray-600">I consent to Bharat Bulk SMS collecting my details and contacting me regarding my inquiry. I have read the <Link href="/privacy-policy" className="text-saffron hover:underline">Privacy Policy</Link>.</span>
                </label>
                <MagneticButton>
                  <button type="submit" className="inline-flex items-center bg-gradient-to-r from-saffron to-[#FF9F1C] hover:from-[#FF9F1C] hover:to-saffron text-white font-bold px-8 py-3.5 rounded-xl shadow-lg shadow-saffron/20 hover:shadow-xl hover:shadow-saffron/30 transition-all duration-300 active:scale-[0.97]">
                    Send Inquiry <ArrowRight className="ml-2 w-4 h-4" />
                  </button>
                </MagneticButton>
              </form>
            </ScrollReveal>
            <ScrollReveal delay={200} direction="right">
              <div className="bg-[#0a0e1a] rounded-2xl p-8 text-white h-full flex flex-col justify-between relative overflow-hidden">
                <div className="absolute inset-0 opacity-[0.04]" style={{
                  backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
                  backgroundSize: '40px 40px'
                }} />
                <div className="absolute top-4 right-4 w-8 h-8 border border-white/[0.06] rotate-45" />
                <div className="absolute bottom-4 left-4 w-5 h-5 border border-saffron/10 rotate-12" />
                <div className="absolute top-0 right-0 w-[200px] h-[200px] bg-gradient-to-bl from-saffron/5 to-transparent rounded-full blur-[50px]" />
                <div className="relative z-10">
                  <h3 className="text-xl font-bold mb-6">Or reach us directly</h3>
                  <div className="space-y-5">
                    <a href="tel:+917208417941" className="flex items-center gap-4 text-white/80 hover:text-saffron transition-colors group">
                      <div className="w-12 h-12 bg-saffron/20 rounded-xl flex items-center justify-center group-hover:bg-saffron/30 group-hover:scale-110 transition-all"><Phone className="w-5 h-5 text-saffron" /></div>
                      <div><p className="text-xs text-white/60">Phone</p><p className="font-medium">+91 7208417941</p></div>
                    </a>
                    <a href="mailto:support@bharatbulksms.com" className="flex items-center gap-4 text-white/80 hover:text-saffron transition-colors group">
                      <div className="w-12 h-12 bg-saffron/20 rounded-xl flex items-center justify-center group-hover:bg-saffron/30 group-hover:scale-110 transition-all"><Send className="w-5 h-5 text-saffron" /></div>
                      <div><p className="text-xs text-white/60">Email</p><p className="font-medium">support@bharatbulksms.com</p></div>
                    </a>
                    <a href="https://wa.me/917208417941" target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 text-white/80 hover:text-saffron transition-colors group">
                      <div className="w-12 h-12 bg-saffron/20 rounded-xl flex items-center justify-center group-hover:bg-saffron/30 group-hover:scale-110 transition-all"><MessageSquare className="w-5 h-5 text-saffron" /></div>
                      <div><p className="text-xs text-white/60">WhatsApp</p><p className="font-medium">+91 7208417941</p></div>
                    </a>
                  </div>
                </div>
                <div className="relative z-10 mt-8 pt-6 border-t border-white/10">
                  <p className="text-sm text-white/80">Office Address</p>
                  <p className="text-sm text-white/70 mt-1">Ostwal Shopping Centre, Opposite Bhayandar East Station, Jesal Park, Bhayandar East, Thane 401105</p>
                  <p className="text-xs text-white/60 mt-2">Mon–Sat: 10:00 AM – 7:00 PM IST</p>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════════
          CAMPAIGN WORKFLOW - Lazy-loaded guidance cards
      ═══════════════════════════════════════════════════════════════════════ */}
      <Suspense fallback={<ComponentSkeleton />}>
        <HomeTestimonials />
      </Suspense>

      {/* ═══════════════════════════════════════════════════════════════════════
          CONTACT OPTIONS
      ═══════════════════════════════════════════════════════════════════════ */}
      <Suspense fallback={<ComponentSkeleton />}>
        <ContactTeamWidget />
      </Suspense>

      {/* ═══════════════════════════════════════════════════════════════════════
          BLOG PREVIEW - Lazy-loaded Latest Articles
      ═══════════════════════════════════════════════════════════════════════ */}
      <Suspense fallback={<ComponentSkeleton />}>
        <HomeBlogPreview />
      </Suspense>

      {/* ═══════════════════════════════════════════════════════════════════════
          FAQ - Dark section with glassmorphism
      ═══════════════════════════════════════════════════════════════════════ */}
      <div className="py-8 bg-white flex justify-center">
        <Suspense fallback={null}>
          <AdSlot slotId="placeholder" format="horizontal" label="Advertisement · 728×90" />
        </Suspense>
      </div>
      <section className="py-20 lg:py-28 bg-[#0a0e1a] relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
          backgroundSize: '50px 50px'
        }} />
        <div className="absolute top-[-10%] left-[-5%] w-[400px] h-[400px] rounded-full bg-gradient-to-br from-saffron/5 to-transparent blur-[80px]" />
        <div className="absolute bottom-[-10%] right-[-5%] w-[350px] h-[350px] rounded-full bg-gradient-to-tl from-blue-500/5 to-transparent blur-[80px]" />
        <div className="absolute top-16 right-[10%] w-16 h-16 border border-white/[0.04] rotate-45 hidden lg:block" />
        <div className="absolute bottom-20 left-[8%] w-10 h-10 border border-saffron/[0.08] rotate-12 hidden lg:block" />
        <div className="absolute top-[30%] right-[4%] w-2 h-2 bg-saffron/30 rounded-full hidden lg:block" />
        <div className="absolute bottom-[40%] left-[3%] w-1.5 h-1.5 bg-blue-400/30 rounded-full hidden lg:block" />
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-white/[0.06] to-transparent" />
        <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-saffron/30 to-transparent" />
        <div className="container relative z-10">
          <div className="grid lg:grid-cols-12 gap-12">
            <ScrollReveal className="lg:col-span-4" direction="left">
              <span className="text-saffron font-bold text-xs uppercase tracking-[0.2em] font-mono">// FAQ</span>
              <h2 className="text-3xl font-extrabold text-white mt-3 mb-4">
                Common questions,<br />straight answers
              </h2>
              <p className="text-white/60 mb-6 text-sm leading-relaxed">
                Everything you need to know about bulk SMS, pricing, and getting started.
              </p>
              <div className="hidden lg:block bg-white/[0.03] border border-white/[0.06] rounded-lg p-4 mb-6 font-mono text-xs">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-2 h-2 rounded-full bg-red-400/70" />
                  <div className="w-2 h-2 rounded-full bg-yellow-400/70" />
                  <div className="w-2 h-2 rounded-full bg-green-400/70" />
                </div>
                <p className="text-white/60"><span className="text-purple-400">const</span> <span className="text-blue-300">faq</span> = <span className="text-saffron">await</span> getAnswers();</p>
                <p className="text-white/60"><span className="text-green-400">// review delivery information after sending</span></p>
              </div>
              <MagneticButton>
                <Link href="/faq" className="inline-flex items-center border border-white/20 text-white hover:bg-white/[0.06] hover:border-white/30 font-medium px-5 py-2.5 rounded-lg transition-all duration-300">
                  View All FAQs <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </MagneticButton>
            </ScrollReveal>
            <div className="lg:col-span-8 space-y-3">
              {faqs.map((faq, i) => (
                <FaqItem key={i} q={faq.q} a={faq.a} index={i} />
              ))}
            </div>
          </div>
        </div>
      </section>
      <div className="py-8 bg-white flex justify-center">
        <Suspense fallback={null}>
          <AdSlot slotId="placeholder" format="responsive" label="Advertisement · Responsive" />
        </Suspense>
      </div>
    </>
  );
}
